import React, { useEffect, useRef, useState } from "react";
import { BiWallet } from "react-icons/bi";

/**
 * Modal "Bayar" — dipakai keempat halaman report kas (Pembayaran AP, Non-PO,
 * Petty Cash HO & SO).
 *
 * Kenapa satu komponen bersama: sejak rilis ini JURNAL TERBIT DI SINI, bukan lagi
 * saat approval final. Menyalin modalnya ke empat halaman berarti empat tempat
 * yang bisa menyimpang soal aturan tanggal — sesuatu yang langsung berakibat ke
 * buku besar.
 *
 * Tanggal wajib: backend menolak yang kosong, dan tanggal inilah yang menjadi
 * tanggal jurnal sekaligus periode laporannya.
 *
 * Default tanggal = TANGGAL DOKUMEN, bukan hari ini. Dokumen yang menunggu
 * pembayaran hampir selalu bertanggal mundur, jadi hari ini adalah tebakan yang
 * hampir selalu salah — dan salahnya tidak kelihatan sampai jurnalnya sudah
 * terbit di periode yang keliru. Hari ini hanya dipakai bila dokumen tidak
 * membawa tanggal yang bisa dibaca.
 */
const ISO_TANGGAL = /^\d{4}-\d{2}-\d{2}/;

const ModalBayar = ({
  show,
  dokumenId,
  nominal,
  tanggalDokumen,
  busy,
  uploadBukti = false,
  onSimpan,
  onBatal,
}) => {
  const hariIni = new Date().toISOString().slice(0, 10);
  const bawaan = ISO_TANGGAL.test(tanggalDokumen || "")
    ? String(tanggalDokumen).slice(0, 10)
    : hariIni;
  const [tanggal, setTanggal] = useState(bawaan);
  // Bukti bayar (opsional, hanya bila uploadBukti): file dipilih di sini, diunggah
  // oleh pemanggil saat Simpan (onSimpan(tanggal, file)).
  const [bukti, setBukti] = useState(null);
  const buktiRef = useRef(null);

  // Reset tiap kali modal dibuka untuk dokumen lain — tanpa ini tanggal pilihan
  // dokumen sebelumnya ikut terbawa dan mudah tersimpan tanpa disadari.
  useEffect(() => {
    if (show) {
      setTanggal(bawaan);
      setBukti(null);
      if (buktiRef.current) buktiRef.current.value = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dokumenId, bawaan]);

  if (!show) return null;

  const kosong = !tanggal;

  const pilihBukti = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ok = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (f.size > 10 * 1024 * 1024 || !ok.includes(f.type)) {
      alert(f.size > 10 * 1024 * 1024 ? "Ukuran file maksimal 10MB." : "Format file harus PDF, JPG, atau PNG.");
      e.target.value = "";
      return;
    }
    setBukti(f);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center gap-3 mb-4 text-blue-600">
          <BiWallet className="text-3xl" />
          <h3 className="text-lg font-semibold text-gray-800">Catat Pembayaran</h3>
        </div>

        <div className="text-sm text-gray-600 mb-4 space-y-1">
          <div>
            Dokumen: <span className="font-mono text-gray-800">{dokumenId}</span>
          </div>
          {nominal != null && nominal !== "" && (
            <div>
              Nominal:{" "}
              <span className="font-semibold text-gray-800">
                Rp {parseFloat(nominal || 0).toLocaleString("id-ID")}
              </span>
            </div>
          )}
        </div>

        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Tanggal pembayaran <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={tanggal}
          onChange={(e) => setTanggal(e.target.value)}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        {tanggal !== hariIni && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
            Tanggal mundur mengikuti tanggal dokumen — jurnal masuk ke periode{" "}
            {new Date(tanggal + "T00:00:00").toLocaleDateString("id-ID", {
              month: "long",
              year: "numeric",
            })}
            , bukan periode berjalan.
          </p>
        )}
        {uploadBukti && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Bukti bayar (opsional)
            </label>
            <input
              ref={buktiRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={pilihBukti}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-500 mt-1">PDF, JPG, atau PNG — maksimal 10MB.</p>
          </div>
        )}
        <p className="text-xs text-gray-500 mb-6">
          Jurnal akan terbit pada tanggal ini dan masuk ke laporan periode tersebut.
          Pastikan tanggalnya benar — pembayaran tidak bisa dibatalkan dari halaman ini.
        </p>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onBatal}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Batal
          </button>
          <button
            onClick={() => onSimpan(tanggal, bukti)}
            disabled={kosong || busy}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalBayar;
